import React from "react";
import { Link } from "react-router-dom";
import LegalLayout from "../../components/LegalLayout";
import useDocumentTitle from "../../hooks/useDocumentTitle";
import { useConsent } from "../../lib/consent";
import { Cookie, Settings, BarChart3, Globe2 } from "lucide-react";
import usePageMeta from "../../hooks/usePageMeta";
import M from "../../lib/pageMetaConfig";

const SECTIONS = [
  { id: "what",         label: "What are cookies?" },
  { id: "categories",   label: "Categories we use" },
  { id: "third-party",  label: "Third-party cookies" },
  { id: "manage",       label: "How to manage" },
  { id: "changes",      label: "Changes" },
  { id: "contact",      label: "Contact" },
];

const PILLARS = [
  { icon: Cookie,    title: "Strict-necessary by default", desc: "Out of the box we set only the cookies needed for auth, CSRF, rate-limiting, and remembering your cookie choice. Nothing more without consent.", accent: "var(--qt-cyan)" },
  { icon: BarChart3, title: "Analytics is opt-in",         desc: "PostHog (EU host) is loaded only after you say yes. We use it to understand which features are valuable and where users hit friction — never to sell or share.", accent: "#1FDD83" },
  { icon: Globe2,    title: "No cross-context advertising", desc: "We don't share data with ad networks, retargeters, or 'identity graphs'. CCPA-equivalent 'do not sell or share' is the default for everyone.", accent: "var(--qt-cyan)" },
  { icon: Settings,  title: "Change your mind any time",    desc: "Open cookie settings from the link below or from the footer of any page. Choices apply immediately and are remembered for 12 months.", accent: "var(--qt-amber)" },
];

export default function Cookies() {
  usePageMeta(M.legalCookies);
  const { openSettings } = useConsent();

  return (
    <LegalLayout
      eyebrow="LEGAL · COOKIES"
      title="Cookie Policy"
      lastUpdated="February 2026"
      version="qortrace-cookies-v1.0"
      status="ACTIVE"
      intro={<>This policy explains what cookies and similar local-storage technologies QorTrace uses, why we use them, and how you can control them. It supplements our <Link to="/legal/privacy">Privacy Policy</Link>.</>}
      stats={[
        { value: "3",     label: "CATEGORIES" },
        { value: "Opt-in", label: "ANALYTICS DEFAULT" },
        { value: "12mo",  label: "CHOICE LIFETIME" },
        { value: "0",     label: "AD-NET TRACKERS" },
      ]}
      pillars={PILLARS}
      sections={SECTIONS}
    >
      <p>
        This Cookie Policy explains what cookies and similar local-storage
        technologies QorTrace uses, why we use them, and how you can control
        them. It supplements our{" "}
        <Link to="/legal/privacy">Privacy Policy</Link>.
      </p>

      <div className="qt-legal-callout">
        <strong>Manage your preferences any time.</strong>{" "}
        <button
          type="button"
          onClick={openSettings}
          data-testid="cookie-policy-open-settings-btn"
          className="underline"
          style={{ color: "#7AE7F2" }}
        >
          Open cookie settings
        </button>
        .
      </div>

      <h2 id="what">1. What are cookies?</h2>
      <p>
        Cookies are small text files placed on your device by a website. We
        also use related technologies such as <code>localStorage</code> and
        <code>sessionStorage</code>, which work similarly. In this policy
        we refer to all of them collectively as &ldquo;cookies.&rdquo;
      </p>

      <h2 id="categories">2. Categories we use</h2>

      <h3>Strictly necessary</h3>
      <p>
        Required for the Service to function. We can&rsquo;t turn these off.
        They handle authentication, CSRF protection, rate-limiting, and
        remembering whether you&rsquo;ve made a cookie choice.
      </p>
      <table className="qt-legal-table">
        <thead>
          <tr>
            <th>Name</th>
            <th>Purpose</th>
            <th>Duration</th>
            <th>Type</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td><code>qt_user_token</code></td>
            <td>Customer session (signed-in account)</td>
            <td>Session / 30 days</td>
            <td>HTTP-only cookie</td>
          </tr>
          <tr>
            <td><code>qt_admin_token</code></td>
            <td>Admin console session</td>
            <td>Session</td>
            <td>HTTP-only cookie</td>
          </tr>
          <tr>
            <td><code>qt_consent_v1</code></td>
            <td>Stores your cookie-consent choice</td>
            <td>12 months</td>
            <td>localStorage</td>
          </tr>
          <tr>
            <td><code>qt_nudge_*</code></td>
            <td>Pins A/B/N variant for consistent UX</td>
            <td>30 days</td>
            <td>Cookie</td>
          </tr>
        </tbody>
      </table>

      <h3>Analytics</h3>
      <p>
        Used only with your consent. Anonymous, aggregated product analytics
        through PostHog so we can understand which features are used and
        where users encounter friction. Form inputs and wallet addresses are
        masked at the source.
      </p>
      <table className="qt-legal-table">
        <thead>
          <tr>
            <th>Name</th>
            <th>Purpose</th>
            <th>Duration</th>
            <th>Provider</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td><code>ph_*</code> / <code>posthog</code></td>
            <td>
              Anonymous product-analytics identifier, page-view tracking,
              feature-flag exposure
            </td>
            <td>12 months</td>
            <td>PostHog</td>
          </tr>
        </tbody>
      </table>

      <h3>Marketing</h3>
      <p>
        Reserved for future advertising and retargeting tags. QorTrace does
        not set any marketing cookies today.
      </p>

      <h2 id="third-party">3. Third-party cookies</h2>
      <p>
        Some features rely on third-party services that may set their own
        cookies subject to their privacy policies:
      </p>
      <ul>
        <li>
          <strong>Stripe</strong> &mdash; checkout pages and fraud-prevention
          signals during payment.
        </li>
        <li>
          <strong>Cloudflare</strong> &mdash; DDoS protection and bot scoring.
        </li>
      </ul>

      <h2 id="manage">4. How to manage cookies</h2>
      <p>You can control cookies in three ways:</p>
      <ul>
        <li>
          Use our{" "}
          <button
            type="button"
            onClick={openSettings}
            className="underline"
            style={{ color: "#7AE7F2" }}
          >
            cookie settings
          </button>{" "}
          to grant or withdraw consent for analytics and marketing.
        </li>
        <li>
          Adjust your browser settings to block or delete cookies.
          Strictly-necessary cookies are required for the Service to function;
          blocking them may break sign-in.
        </li>
        <li>
          Use Global Privacy Control (GPC) browser settings &mdash; we honor
          GPC signals as a do-not-share request.
        </li>
      </ul>

      <h2 id="changes">5. Changes</h2>
      <p>
        We will update this policy when we add or remove cookies. The
        &ldquo;Last updated&rdquo; date above reflects the most recent change.
      </p>

      <h2 id="contact">6. Contact</h2>
      <p>
        Questions? Email{" "}
        <a href="mailto:privacy@qortrace.com">privacy@qortrace.com</a>.
      </p>
    </LegalLayout>
  );
}
